import React, { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, Text } from '@/components/ui';
import { showAlert } from '@/components/ui/alert';
import { clearLogs, getLogs, subscribe, uploadLogs } from '@/libs/debug-log';
import type { DebugLogEntry } from '@/libs/debug-log';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type Props = { open: boolean; onOpenChange: (open: boolean) => void };

type Filter = 'all' | 'console' | 'network';

function useDebugLogs() {
  return useSyncExternalStore(subscribe, getLogs, getLogs);
}

function formatTime(ts: number) {
  const d = new Date(ts);
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${`${d.getMilliseconds()}`.padStart(3, '0')}`;
}

function levelColor(entry: DebugLogEntry): string {
  if (entry.kind === 'console') {
    if (entry.level === 'error') return guardianColors.Error;
    if (entry.level === 'warn') return '#B7791F';
    return guardianColors['10'];
  }
  if (entry.error || (entry.status != null && entry.status >= 400)) return guardianColors.Error;
  return '#2F855A';
}

function summarize(entry: DebugLogEntry): string {
  if (entry.kind === 'console') {
    return entry.message.split('\n')[0].slice(0, 200);
  }
  const status = entry.status != null ? `${entry.status}` : entry.error ? 'ERR' : '···';
  const duration = entry.durationMs != null ? ` ${entry.durationMs}ms` : '';
  return `${status} ${entry.method} ${entry.url}${duration}`;
}

export function DebugLogSheet({ open, onOpenChange }: Props) {
  const styles = useStyles();
  const { scale } = useScaling();
  const logs = useDebugLogs();

  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<DebugLogEntry | null>(null);
  const [sending, setSending] = useState(false);

  const visible = useMemo(() => {
    const list = filter === 'all' ? logs : logs.filter(l => l.kind === filter);
    return list.slice().reverse();
  }, [logs, filter]);

  const handleClose = useCallback(() => {
    setSelected(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleClear = useCallback(() => {
    clearLogs();
    setSelected(null);
  }, []);

  const handleSend = useCallback(async () => {
    if (sending) return;
    setSending(true);
    try {
      const { path } = await uploadLogs();
      showAlert({ title: 'Logs sent', message: `Uploaded ${logs.length} entries to\n${path}` });
    } catch (error) {
      showAlert({
        title: 'Upload failed',
        message: error instanceof Error ? error.message : 'Could not upload logs.',
      });
    } finally {
      setSending(false);
    }
  }, [logs.length, sending]);

  const renderFilterTab = (value: Filter, label: string) => {
    const active = filter === value;
    return (
      <Pressable
        key={value}
        onPress={() => setFilter(value)}
        style={[styles.tab, active ? styles.tabActive : null]}>
        <Text fontSize={scale(12)} fontWeight={active ? '600' : '400'} style={styles.tabText}>
          {label}
        </Text>
      </Pressable>
    );
  };

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent closeOnBackdropPress style={styles.sheet}>
        <View style={styles.grabber} />

        <Stack direction="row" align="center" justify="space-between" style={styles.header}>
          <Text fontSize={scale(16)} fontWeight="700" style={styles.title}>
            Debug logs ({logs.length})
          </Text>
          <Stack direction="row" align="center" style={styles.headerActions}>
            <Pressable onPress={handleClear} style={styles.actionBtn}>
              <Text fontSize={scale(13)} style={styles.clearText}>
                Clear
              </Text>
            </Pressable>
            <Pressable
              onPress={handleSend}
              disabled={sending}
              style={[styles.actionBtn, styles.sendBtn]}>
              {sending ? (
                <ActivityIndicator size="small" color={colors['0']} />
              ) : (
                <Text fontSize={scale(13)} fontWeight="600" style={styles.sendText}>
                  Send
                </Text>
              )}
            </Pressable>
            <Pressable onPress={handleClose} style={styles.actionBtn}>
              <Text fontSize={scale(13)} style={styles.closeText}>
                Close
              </Text>
            </Pressable>
          </Stack>
        </Stack>

        <Stack direction="row" align="center" style={styles.tabs}>
          {renderFilterTab('all', 'All')}
          {renderFilterTab('console', 'Console')}
          {renderFilterTab('network', 'Network')}
        </Stack>

        {selected ? (
          <View style={styles.detailWrap}>
            <Pressable onPress={() => setSelected(null)} style={styles.backRow}>
              <Text fontSize={scale(13)} fontWeight="600" style={styles.backText}>
                ‹ Back to list
              </Text>
            </Pressable>
            <ScrollView style={styles.detailScroll} contentContainerStyle={styles.detailContent}>
              <DetailBody entry={selected} scale={scale} styles={styles} />
            </ScrollView>
          </View>
        ) : (
          <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {visible.length === 0 ? (
              <Text fontSize={scale(13)} style={styles.empty}>
                No logs captured yet.
              </Text>
            ) : (
              visible.map(entry => (
                <Pressable key={entry.id} onPress={() => setSelected(entry)} style={styles.row}>
                  <Text fontSize={scale(10)} style={styles.rowTime}>
                    {formatTime(entry.ts)}
                  </Text>
                  <Text
                    fontSize={scale(12)}
                    numberOfLines={2}
                    style={[styles.rowText, { color: levelColor(entry) }]}>
                    {summarize(entry)}
                  </Text>
                </Pressable>
              ))
            )}
          </ScrollView>
        )}
      </SheetContent>
    </SheetModal>
  );
}

type DetailStyles = ReturnType<typeof useStyles>;

function DetailField({
  label,
  value,
  scale,
  styles,
}: {
  label: string;
  value?: string;
  scale: (n: number) => number;
  styles: DetailStyles;
}) {
  if (!value) return null;
  return (
    <View style={styles.field}>
      <Text fontSize={scale(11)} fontWeight="700" style={styles.fieldLabel}>
        {label}
      </Text>
      <Text fontSize={scale(11)} style={styles.fieldValue}>
        {value}
      </Text>
    </View>
  );
}

function DetailBody({
  entry,
  scale,
  styles,
}: {
  entry: DebugLogEntry;
  scale: (n: number) => number;
  styles: DetailStyles;
}) {
  const field = (label: string, value?: string) => (
    <DetailField label={label} value={value} scale={scale} styles={styles} />
  );

  if (entry.kind === 'console') {
    return (
      <>
        {field('Time', new Date(entry.ts).toISOString())}
        {field('Level', entry.level)}
        {field('Message', entry.message)}
      </>
    );
  }
  return (
    <>
      {field('Time', new Date(entry.ts).toISOString())}
      {field('Request', `${entry.method} ${entry.url}`)}
      {field('Status', entry.status != null ? `${entry.status}` : undefined)}
      {field('Duration', entry.durationMs != null ? `${entry.durationMs} ms` : undefined)}
      {field('Error', entry.error)}
      {field('Request body', entry.requestBody)}
      {field('Response body', entry.responseBody)}
    </>
  );
}

const useStyles = createStyles(({ scale }) => ({
  sheet: { maxHeight: '85%' },
  grabber: {
    alignSelf: 'center',
    width: scale(40),
    height: scale(4),
    borderRadius: scale(2),
    backgroundColor: guardianColors['3'],
    marginTop: scale(8),
    marginBottom: scale(8),
  },
  header: { paddingHorizontal: scale(20), paddingBottom: scale(8) },
  title: { color: guardianColors['10'] },
  headerActions: { gap: scale(6) },
  actionBtn: {
    paddingHorizontal: scale(10),
    paddingVertical: scale(6),
    borderRadius: scale(8),
  },
  sendBtn: { backgroundColor: guardianColors['10'], minWidth: scale(52), alignItems: 'center' },
  sendText: { color: colors['0'] },
  clearText: { color: guardianColors.Error },
  closeText: { color: guardianColors['7'] },
  tabs: { paddingHorizontal: scale(20), gap: scale(8), marginBottom: scale(8) },
  tab: {
    paddingHorizontal: scale(14),
    paddingVertical: scale(6),
    borderRadius: scale(16),
    backgroundColor: guardianColors['1'],
  },
  tabActive: { backgroundColor: guardianColors['3'] },
  tabText: { color: guardianColors['10'] },
  list: { maxHeight: scale(420) },
  listContent: { paddingHorizontal: scale(20), paddingBottom: scale(16) },
  empty: { color: guardianColors['7'], textAlign: 'center', paddingVertical: scale(24) },
  row: {
    paddingVertical: scale(8),
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['1'],
  },
  rowTime: { color: guardianColors['7'], marginBottom: scale(2) },
  rowText: { fontFamily: 'Courier', lineHeight: scale(16) },
  detailWrap: { maxHeight: scale(440) },
  backRow: { paddingHorizontal: scale(20), paddingBottom: scale(8) },
  backText: { color: guardianColors['10'] },
  detailScroll: { maxHeight: scale(400) },
  detailContent: { paddingHorizontal: scale(20), paddingBottom: scale(24) },
  field: { marginBottom: scale(12) },
  fieldLabel: { color: guardianColors['7'], marginBottom: scale(2) },
  fieldValue: { color: guardianColors['10'], fontFamily: 'Courier' },
}));
